import React, { useRef, useState } from 'react';
import { FileText, Copy, Check, Eye, X } from 'lucide-react';
import type { GenConfig, OrganizationType } from '../../../types';
import { generateMode2Caption } from '../../../services/api';
import { refreshWallet } from '../../../hooks/useWallet';
import AlertDialog from '../../../components/AlertDialog';

interface CaptionPanelProps {
    mode2Result: { imageUrl: string; gcsPath: string } | null;
    genConfig: GenConfig;
    name: string;
    title?: string | null;
    organizationName?: string | null;
    organizationType: OrganizationType;
    variant?: 'compact' | 'full';
    /** compact 模式時可塞入額外按鈕（例如下載），與「生成文案」共用同一列 */
    actionButton?: React.ReactNode;
}

const CaptionPanel: React.FC<CaptionPanelProps> = ({
    mode2Result, genConfig, name, title, organizationName, organizationType, variant = 'full', actionButton,
}) => {
    const [captionResult, setCaptionResult] = useState<{ plainText: string; markdown: string } | null>(null);
    const [isCaptionLoading, setIsCaptionLoading] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [pointsExhaustedMessage, setPointsExhaustedMessage] = useState<string | null>(null);
    const captionResultRef = useRef<HTMLDivElement>(null);

    const handleGenerateCaption = async () => {
        if (!mode2Result) return;
        setIsCaptionLoading(true);
        try {
            const result = await generateMode2Caption({
                imageUrl: mode2Result.imageUrl,
                genConfig,
                name,
                title,
                organizationName,
                organizationType,
            });
            setCaptionResult(result);
            setIsPopupOpen(true);
            refreshWallet();
        } catch (e: any) {
            console.error(e);
            if (e.isPointsExhausted) {
                setPointsExhaustedMessage(e.message || '本月點數已用完，請聯繫客服為您調整！');
            } else {
                alert('文案生成失敗，請稍後再試。');
            }
        } finally {
            setIsCaptionLoading(false);
        }
    };

    const handleCopyText = async (text: string, key: string) => {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    if (variant === 'compact') {
        return (
            <>
                <div className="flex gap-2 shrink-0">
                    <button
                        onClick={handleGenerateCaption}
                        disabled={isCaptionLoading}
                        className={`flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-400 transition-all flex items-center justify-center gap-2 text-sm ${isCaptionLoading ? 'opacity-70 cursor-wait' : ''}`}
                    >
                        {isCaptionLoading
                            ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> 生成中...</>
                            : <><FileText size={15} /> 生成文案</>}
                    </button>

                    {captionResult && (
                        <button
                            onClick={() => setIsPopupOpen(true)}
                            className="flex-1 py-2.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 rounded-xl font-bold hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-all flex items-center justify-center gap-2 text-sm"
                        >
                            <Eye size={15} /> 查看文案
                        </button>
                    )}

                    {actionButton}
                </div>

                {/* Caption Popup */}
                {isPopupOpen && captionResult && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
                        onClick={() => setIsPopupOpen(false)}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-[90vw] max-w-lg max-h-[80vh] flex flex-col"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
                                <span className="font-bold text-amber-500 dark:text-amber-400 flex items-center gap-1.5 text-sm">
                                    <FileText size={15} /> 生成文案結果
                                </span>
                                <button onClick={() => setIsPopupOpen(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
                                <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-slate-500">純文字版</span>
                                        <button
                                            onClick={() => handleCopyText(captionResult.plainText, 'plain')}
                                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                                        >
                                            {copiedKey === 'plain' ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                                            {copiedKey === 'plain' ? '已複製' : '複製'}
                                        </button>
                                    </div>
                                    <pre className="text-slate-600 dark:text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-sans">
                                        {captionResult.plainText}
                                    </pre>
                                </div>
                                {/* markdown 版本 先關閉 */}
                                {/* <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-slate-500">Markdown 版</span>
                                        <button
                                            onClick={() => handleCopyText(captionResult.markdown, 'md')}
                                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                                        >
                                            {copiedKey === 'md' ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                                            {copiedKey === 'md' ? '已複製' : '複製'}
                                        </button>
                                    </div>
                                    <pre className="text-slate-600 dark:text-slate-300 text-xs leading-relaxed whitespace-pre-wrap font-mono">
                                        {captionResult.markdown}
                                    </pre>
                                </div> */}
                            </div>
                        </div>
                    </div>
                )}

                {copiedKey && (
                    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-sm font-medium px-5 py-2.5 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-300 pointer-events-none">
                        已複製到剪貼簿
                    </div>
                )}

                {pointsExhaustedMessage && (
                    <AlertDialog
                        title="點數不足"
                        message={pointsExhaustedMessage}
                        severity="warning"
                        onClose={() => setPointsExhaustedMessage(null)}
                    />
                )}
            </>
        );
    }

    // 'full' variant
    return (
        <>
            <button
                onClick={handleGenerateCaption}
                disabled={isCaptionLoading}
                className={`w-full py-4 bg-amber-500 text-white rounded-2xl font-bold hover:bg-amber-400 transition-all flex items-center justify-center gap-2 ${isCaptionLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
                {isCaptionLoading ? (
                    <><div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /> 生成中...</>
                ) : (
                    <><FileText size={20} /> 生成文案</>
                )}
            </button>

            {captionResult && (
                <div ref={captionResultRef} className="mt-8 space-y-6 animate-in fade-in duration-500">
                    <h3 className="text-lg font-bold text-amber-500 dark:text-amber-400">生成文案結果</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-bold text-slate-600 dark:text-slate-300">純文字版</h4>
                                <button
                                    onClick={() => handleCopyText(captionResult.plainText, 'plain')}
                                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    {copiedKey === 'plain' ? <Check size={14} className="text-emerald-500 dark:text-emerald-400" /> : <Copy size={14} />}
                                    {copiedKey === 'plain' ? '已複製' : '複製'}
                                </button>
                            </div>
                            <pre className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                                {captionResult.plainText}
                            </pre>
                        </section>
                        <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-bold text-slate-600 dark:text-slate-300">Markdown 版</h4>
                                <button
                                    onClick={() => handleCopyText(captionResult.markdown, 'md')}
                                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    {copiedKey === 'md' ? <Check size={14} className="text-emerald-500 dark:text-emerald-400" /> : <Copy size={14} />}
                                    {copiedKey === 'md' ? '已複製' : '複製'}
                                </button>
                            </div>
                            <pre className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-mono text-xs">
                                {captionResult.markdown}
                            </pre>
                        </section>
                    </div>
                </div>
            )}

            {copiedKey && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-sm font-medium px-5 py-2.5 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-300 pointer-events-none">
                    已複製到剪貼簿
                </div>
            )}

            {pointsExhaustedMessage && (
                <AlertDialog
                    title="點數不足"
                    message={pointsExhaustedMessage}
                    severity="warning"
                    onClose={() => setPointsExhaustedMessage(null)}
                />
            )}
        </>
    );
};

export default CaptionPanel;
